import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    List, ListItem, ListItemIcon, ListItemText, Typography,
    Breadcrumbs, IconButton, Box, CircularProgress
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import HomeIcon from '@mui/icons-material/Home';
import axios from 'axios';
import { useSelector } from 'react-redux';

const FolderSelector = ({ open, onClose, onSelect, mode = 'internal', title }) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [parent, setParent] = useState(null); // Internal: ID, Google: ID
    const [breadcrumbs, setBreadcrumbs] = useState([]); // Array of { id, name }
    const token = useSelector(state => state.auth.token);

    // Reset state when dialog opens or mode changes
    useEffect(() => {
        if (open) {
            setParent(null);
            setBreadcrumbs([]);
            fetchFiles(null);
        }
    }, [open, mode]);

    const fetchFiles = async (folderId) => {
        setLoading(true);
        try {
            if (mode === 'google') {
                const { data } = await axios.get('/api/integrations/google/list', {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { folderId: folderId || 'root' }
                });
                // Google Drive returns mixed content, filter for folders
                const foldersOnly = data.filter(f => f.mimeType === 'application/vnd.google-apps.folder');
                setFiles(foldersOnly);
            } else if (mode === 'dropbox') {
                const { data } = await axios.get('/api/dropbox/list', {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { path: folderId || '' } // folderId is the path in Dropbox
                });
                // Dropbox returns mixed content, filter for folders
                // We map '.tag' to 'type' in our dropbox service listFiles
                const foldersOnly = data.filter(f => f.type === 'folder');
                setFiles(foldersOnly);
            } else {
                // Internal File Manager
                const { data } = await axios.get('/api/files', {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { parent: folderId || '' }
                });
                // Internal returns mixed content, filter for folders
                const foldersOnly = data.filter(f => f.type === 'folder');
                setFiles(foldersOnly);
            }
        } catch (err) {
            console.error("Failed to fetch folders", err);
            setFiles([]);
        } finally {
            setLoading(false);
        }
    };

    const handleEnterFolder = (folder) => {
        let id, name;
        if (mode === 'google') {
            id = folder.id;
            name = folder.name;
        } else if (mode === 'dropbox') {
            id = folder.path_lower; // Use path_lower for API calls
            name = folder.name;
        } else {
            id = folder._id;
            name = folder.name;
        }

        setBreadcrumbs(prev => [...prev, { id, name }]);
        setParent(id);
        fetchFiles(id);
    };

    const handleNavigateBreadcrumb = (index) => {
        if (index === -1) {
            // Root
            setParent(null);
            setBreadcrumbs([]);
            fetchFiles(null);
            return;
        }

        const target = breadcrumbs[index];
        const newCrumbs = breadcrumbs.slice(0, index + 1);
        setBreadcrumbs(newCrumbs);
        setParent(target.id);
        fetchFiles(target.id);
    };

    const handleUp = () => {
        if (breadcrumbs.length === 0) return;

        const newCrumbs = [...breadcrumbs];
        newCrumbs.pop();
        setBreadcrumbs(newCrumbs);

        const newParent = newCrumbs.length > 0 ? newCrumbs[newCrumbs.length - 1].id : null;
        setParent(newParent);
        fetchFiles(newParent);
    };

    const handleSelectCurrent = () => {
        onSelect(parent);
    };

    const getTitle = () => {
        if (title) return title;
        if (mode === 'google') return "Select Google Drive Folder";
        if (mode === 'dropbox') return "Select Dropbox Folder";
        return "Select Destination Folder";
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{getTitle()}</DialogTitle>
            <DialogContent dividers sx={{ height: 400, display: 'flex', flexDirection: 'column' }}>
                {/* Navigation Bar */}
                <Box display="flex" alignItems="center" mb={2} p={1} bgcolor="#f5f5f5" borderRadius={1}>
                    <IconButton size="small" onClick={handleUp} disabled={!parent}>
                        <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <Breadcrumbs maxItems={3} sx={{ ml: 2, flex: 1 }}>
                        <Typography
                            variant="body2"
                            color={!parent ? "text.primary" : "inherit"}
                            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', fontWeight: !parent ? 'bold' : 'normal' }}
                            onClick={() => handleNavigateBreadcrumb(-1)}
                        >
                            <HomeIcon fontSize="small" sx={{ mr: 0.5 }} /> Root
                        </Typography>
                        {breadcrumbs.map((crumb, idx) => {
                            const isLast = idx === breadcrumbs.length - 1;
                            return (
                                <Typography
                                    key={crumb.id}
                                    variant="body2"
                                    color={isLast ? "text.primary" : "inherit"}
                                    sx={{ cursor: isLast ? 'default' : 'pointer', fontWeight: isLast ? 'bold' : 'normal' }}
                                    onClick={() => !isLast && handleNavigateBreadcrumb(idx)}
                                >
                                    {crumb.name}
                                </Typography>
                            );
                        })}
                    </Breadcrumbs>
                </Box>

                {/* Folder List */}
                <Box flex={1} overflow="auto">
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <List dense>
                            {files.length === 0 && (
                                <Typography variant="body2" color="text.secondary" align="center" mt={4}>
                                    No subfolders found.
                                </Typography>
                            )}
                            {files.map(folder => (
                                <ListItem
                                    button
                                    key={mode === 'google' ? folder.id : (mode === 'dropbox' ? folder.path_lower : folder._id)}
                                    onClick={() => handleEnterFolder(folder)}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <FolderIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={folder.name} />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSelectCurrent}
                    color="primary"
                >
                    Select {breadcrumbs.length > 0 ? `"${breadcrumbs[breadcrumbs.length - 1].name}"` : "Root"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FolderSelector;
